import Dashboard from "layouts/dashboard";
import Tables from "layouts/tables";
import Billing from "layouts/billing";
import Profile from "layouts/profile";

import SignIn from "layouts/authentication/sign-in";
import SignUp from "layouts/authentication/sign-up";
import SignOut from "layouts/authentication/sign-out";
import SignUpForm from "layouts/authentication/sign-up-form";

import { ProtectedRoute } from "context/ProtectedRoute";
import Icon from "@mui/material/Icon";

import HostLocations from "components/Locations";
import EditHost from "components/Locations/editLocation";
import Notifications from "layouts/notifications";
import LocationActivity from "components/LocationActivity";
import LocationActivityDetails from "components/LocationActivity/activityDetail";
import LocationSuspendedUsersPage from "components/LocationSuspendedUsers";
import PoorConditionReports from "components/LocationPoorConditionReports";
import AddLocation from "components/Locations/addLocation";
import CorporateHostRatings from "components/Corporate/CorporateRatingsList";
import Support from "components/Corporate/Support";

import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { STRIPE_CONFIG } from "constants";
import ViewLocation from "components/Locations/viewLocation";
import LearnMore from "layouts/authentication/learn-more";
import UserGuide from "components/Corporate/UserGuide";

// Avoid crashing the entire route tree when a deployment is missing Stripe configuration.
// Paid flows still require REACT_APP_STRIPE_PUBLISH_KEY to be configured.
const stripePromise = STRIPE_CONFIG.PUB_KEY ? loadStripe(STRIPE_CONFIG.PUB_KEY) : null;

export default function getRoutes() {
  return [
    // ======================
    // PROTECTED ROUTES
    // ======================
    {
      type: "collapse",
      name: "Dashboard",
      key: "dashboard",
      icon: <Icon fontSize="small">dashboard</Icon>,
      route: "/dashboard",
      component: (
        <ProtectedRoute>
          <Dashboard />
        </ProtectedRoute>
      ),
    },
    {
      type: "collapse",
      name: "Support",
      key: "support",
      icon: <Icon fontSize="small">support</Icon>,
      route: "/support",
      component: (
        <ProtectedRoute>
          <Support />
        </ProtectedRoute>
      ),
      hideInSidebar: true,
    },
    {
      type: "collapse",
      name: "Locations",
      key: "locations",
      icon: <Icon fontSize="small">location_on</Icon>,
      route: "/locations",
      component: (
        <ProtectedRoute>
          <HostLocations />
        </ProtectedRoute>
      ),
    },
    {
      type: "collapse",
      name: "Add Location",
      key: "add-location",
      icon: <Icon fontSize="small">add</Icon>,
      route: "/location/add",
      component: (
        <ProtectedRoute>
          <Elements stripe={stripePromise}>
            <AddLocation />
          </Elements>
        </ProtectedRoute>
      ),
    },
    {
      type: "collapse",
      name: "Profile",
      key: "profile",
      icon: <Icon fontSize="small">person</Icon>,
      route: "/profile",
      component: (
        <ProtectedRoute>
          <Profile />
        </ProtectedRoute>
      ),
    },
    {
      type: "collapse",
      name: "User Guide",
      key: "user-guide",
      icon: <Icon fontSize="small">help</Icon>,
      route: "/user-guide",
      component: (
        <ProtectedRoute>
          <UserGuide />
        </ProtectedRoute>
      ),
    },
    {
      type: "collapse",
      name: "Edit Host",
      key: "edit-host",
      icon: <Icon fontSize="small">edit</Icon>,
      route: "/location/edit/:id",
      component: (
        <ProtectedRoute>
          <EditHost />
        </ProtectedRoute>
      ),
      hideInSidebar: true,
    },
    {
      type: "collapse",
      name: "View Host",
      key: "view-host",
      icon: <Icon fontSize="small">view</Icon>,
      route: "/location/:id",
      component: (
        <ProtectedRoute>
          <ViewLocation />
        </ProtectedRoute>
      ),
      hideInSidebar: true,
    },
    {
      type: "collapse",
      name: "Location Ratings",
      key: "location-ratings",
      icon: <Icon fontSize="small">star</Icon>,
      route: "/location/ratings/:id",
      component: (
        <ProtectedRoute>
          <CorporateHostRatings />
        </ProtectedRoute>
      ),
      hideInSidebar: true,
    },
    {
      type: "collapse",
      name: "Location Activity",
      key: "location-activity",
      icon: <Icon fontSize="small">list</Icon>,
      route: "/location/activity/:id",
      component: (
        <ProtectedRoute>
          <LocationActivity />
        </ProtectedRoute>
      ),
      hideInSidebar: true,
    },
    {
      type: "collapse",
      name: "Location Activity Detail",
      key: "location-activity-detail",
      icon: <Icon fontSize="small">info</Icon>,
      route: "/location/activity/detail/:id",
      component: (
        <ProtectedRoute>
          <LocationActivityDetails />
        </ProtectedRoute>
      ),
      hideInSidebar: true,
    },
    {
      type: "collapse",
      name: "Location Suspended Users",
      key: "location-suspended-users",
      icon: <Icon fontSize="small">block</Icon>,
      route: "/location/suspended-users/:id",
      component: (
        <ProtectedRoute>
          <LocationSuspendedUsersPage />
        </ProtectedRoute>
      ),
      hideInSidebar: true,
    },
    {
      type: "collapse",
      name: "Location Poor Condition Reports",
      key: "location-poor-condition-reports",
      icon: <Icon fontSize="small">report</Icon>,
      route: "/location/poor-condition-reports/:id",
      component: (
        <ProtectedRoute>
          <PoorConditionReports />
        </ProtectedRoute>
      ),
      hideInSidebar: true,
    },

    // ======================
    // PUBLIC ROUTES
    // ======================
    {
      type: "collapse",
      name: "Sign In",
      key: "sign-in",
      icon: <Icon fontSize="small">login</Icon>,
      route: "/authentication/sign-in",
      component: <SignIn />,
      hideInSidebar: true,
    },
    {
      type: "collapse",
      name: "Sign Out",
      key: "sign-out",
      icon: <Icon fontSize="small">logout</Icon>,
      route: "/authentication/sign-out",
      component: <SignOut />,
    },
    {
      type: "collapse",
      name: "Sign Up",
      key: "sign-up",
      icon: <Icon fontSize="small">assignment</Icon>,
      route: "/authentication/sign-up",
      component: <SignUp />,
      hideInSidebar: true,
    },
    {
      type: "collapse",
      name: "Learn More",
      key: "learn-more",
      icon: <Icon fontSize="small">assignment</Icon>,
      route: "/authentication/learn-more",
      component: <LearnMore />,
      hideInSidebar: true,
    },
    {
      type: "collapse",
      name: "Sign Up Form",
      key: "sign-up-form",
      icon: <Icon fontSize="small">assignment</Icon>,
      route: "/authentication/sign-up-form",
      component: <SignUpForm />,
      hideInSidebar: true,
    },
  ];
}
